/**
 * The Clinical Intelligence Chamber: a sculpted hall 26 m wide and 26.6 m deep under a 6 m
 * ceiling (architecture in chamber.ts, plan in plan.ts, design in docs/chamber.md), built once
 * and themed per study. From the entrance (+z wall): the reception (Luma, the host) on the right, the presentation display on the entrance wall
 * west of the door, the central table, and the centre screen facing it with the dashboards
 * either side, all set into the curved north wall. The collaboration studio (whiteboard, tray,
 * model shelf) opens off the west wall; the patient ward stands on the east side and runs on
 * through an arch into its own room, sized by the study's subject count.
 */
import * as THREE from 'three';
import { DASHBOARD_ARC_ORDER, DASHBOARD_TITLES, type DashboardId } from '@clinspace/shared';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ShellBuilder, instanced, type RoomBounds } from './shell.ts';
import {
  buildAtmosphere,
  buildBackdrop,
  buildCeiling,
  buildFloor,
  buildHelixColumns,
  buildPlants,
  buildWalls,
  planShape,
  type ChamberTime,
} from './chamber.ts';
import { makeSign } from './signage.ts';
import { buildChairGeometry } from './chair.ts';
import { BODY_FONT, DISPLAY_FONT } from '../ui/theme.ts';
import { buildHorizon, type HorizonData } from './horizon.ts';
import {
  ARC,
  CENTRE,
  HALL,
  PRESENTATION,
  RECEPTION,
  SPAWN,
  STUDIO,
  STUDIO_FITTINGS,
  ARC_CENTRE,
  TABLE,
  TABLE_STAGE,
  NORTH_WALL,
  arcPlan,
  centreWall as centreWallPlan,
  hallOutline,
  hallWallRuns,
  receptionPoint,
  wardArch,
} from './plan.ts';
import type { WalkableBounds } from '../xr/Locomotion.ts';
import { WARD as WARD_AREA, walkableOutline, wardRoom, type WardRoom } from '../ward/layout.ts';

export { PRESENTATION, STUDIO_FITTINGS } from './plan.ts';

export const ROOM = {
  width: HALL.halfWidth * 2,
  depth: HALL.front - HALL.back,
  height: 6,
  entranceZ: HALL.front,
  /** The top of the curved north wall (see plan.ts). */
  backZ: HALL.back,
  spawn: new THREE.Vector3(SPAWN.x, 0, SPAWN.z),
  lookAt: new THREE.Vector3(0, 1.3, 0),
  tableCentre: new THREE.Vector3(TABLE.x, 0, TABLE.z),
  /** The centre of the curve the dashboards and the north wall stand on. */
  arcCentre: new THREE.Vector3(ARC_CENTRE.x, 0, ARC_CENTRE.z),
  // Eleven 2.4 m panels either side of the centre screen; each has a floor stage in front.
  arcRadius: ARC.radius,
  panelWidth: ARC.panelWidth,
  panelHeight: ARC.panelHeight,
  panelCentreY: ARC.panelCentreY,
};

export { walkableOutline } from '../ward/layout.ts';

export interface StudyTheme {
  code: string;
  title: string;
  shortTitle: string;
  phase: string;
  therapeuticArea: string;
  accent: string;
}

/** What the data horizon above the dashboards shows (see horizon.ts). */
export type HeaderData = HorizonData;

export interface ArcPanel {
  id: DashboardId;
  title: string;
  group: THREE.Group;
  face: THREE.Mesh;
  statusLight: THREE.Mesh;
  handle: THREE.Mesh;
  /** The placeholder title sign; hidden once a live dashboard takes the frame. */
  sign: THREE.Object3D;
  position: THREE.Vector3;
  yaw: number;
}

export interface StudyRoomWorld {
  group: THREE.Group;
  bounds: WalkableBounds;
  floors: THREE.Object3D[];
  panels: ArcPanel[];
  exitZone: { minX: number; maxX: number; minZ: number; maxZ: number };
  lecternPosition: THREE.Vector3;
  /** The ward room behind the east wall's arch, sized for the study's subjects. */
  wardRoom: WardRoom;
  /** Where the centre screen stands: its centre, its yaw, its scale over a dashboard panel. */
  centreScreen: { position: THREE.Vector3; yaw: number; scale: number };
  setHeader(data: HeaderData): void;
  /**
   * What the centre screen's place shows until the study's dashboards have loaded: loading,
   * or what went wrong. Undefined hides it (the live centre screen stands there).
   */
  setStatus(status: string | undefined, isError?: boolean): void;
  update(dt: number): void;
  dispose(): void;
}

/**
 * The chamber's light: a warm key from above and a warm fill (the afternoon of the retired
 * time-of-day setting); the rest comes from the architecture.
 */
const LIGHT = {
  sun: '#ffe9d0',
  intensity: 1.5,
  position: [-3, 12, 8] as [number, number, number],
  fill: 1.1,
};

/**
 * The Clinical Intelligence Chamber's materials (see chamber.ts): warm stone, warm graphite,
 * satin titanium, champagne aluminium, bronze and frosted crystal glass. No white, no black.
 */
const C = {
  stone: '#6c645b',
  stoneDark: '#4f4842',
  graphite: '#3e3833',
  titanium: '#a8a39c',
  champagne: '#c9b28c',
  bronze: '#9a7550',
  glass: '#9d958a',
  whiteboard: '#f4f1ec',
  screen: '#2b2724',
  fabric: '#b3a697',
  tableTop: '#d2c7b8',
  fabricDark: '#8f8273',
  sign: '#cdb58f',
};

export function buildStudyRoom(
  theme: StudyTheme,
  _renderer: THREE.WebGLRenderer,
  /** The study's subject count: the ward room is sized for all of them. */
  subjectCount: number,
): StudyRoomWorld {
  const { width, height } = ROOM;
  const hx = width / 2;
  const hz = HALL.front;
  const bounds: RoomBounds = { minX: STUDIO.x0, maxX: hx, minZ: ROOM.backZ, maxZ: hz, height };
  const room = wardRoom(subjectCount);
  const arch = wardArch();
  const plan = arcPlan(DASHBOARD_ARC_ORDER.length);
  const shell = new ShellBuilder(bounds);
  const T = ROOM.tableCentre;
  const A = ROOM.arcCentre;
  const time: ChamberTime = { value: 0 };
  const accent = new THREE.Color(luminousSoft(theme.accent));

  /* ---------- Lines of light in the architecture: one mesh, the accent colour ---------- */
  const glow: THREE.BufferGeometry[] = [];
  const ring = (
    r: number,
    w: number,
    x: number,
    y: number,
    z: number,
    from = 0,
    to = Math.PI * 2,
  ) => {
    // Facing up; the arc's angle a (x = cos a, z = sin a) is RingGeometry's theta = -a.
    glow.push(
      new THREE.RingGeometry(r - w / 2, r + w / 2, 160, 1, -to, to - from)
        .rotateX(-Math.PI / 2)
        .translate(x, y, z),
    );
  };
  const bar = (w: number, h: number, d: number, x: number, y: number, z: number, rotY = 0) =>
    glow.push(new THREE.BoxGeometry(w, h, d).rotateY(rotY).translate(x, y, z));
  /** Lines in the study's colour: the floor and table rings and the chair feet. */
  const cool: THREE.BufferGeometry[] = [];
  const coolRing = (
    r: number,
    w: number,
    x: number,
    y: number,
    z: number,
    from = 0,
    to = Math.PI * 2,
  ) =>
    cool.push(
      new THREE.RingGeometry(r - w / 2, r + w / 2, 160, 1, -to, to - from)
        .rotateX(-Math.PI / 2)
        .translate(x, y, z),
    );
  /** The chamber's architectural light: warm white, as in the user's reference. */
  const WARM = new THREE.Color('#ffdcae');

  /* ---------- Floor ---------- */
  const floor = buildFloor(T, [hallOutline(), room.floor], A);
  // Seams of light in the floor: round the table, along the arc and from the door.
  coolRing(2.95, 0.03, T.x, 0.006, T.z);
  coolRing(3.35, 0.015, T.x, 0.006, T.z);
  coolRing(4.4, 0.02, T.x, 0.006, T.z);
  ring(ARC.radius - 1.35, 0.025, A.x, 0.006, A.z, plan.start, plan.end);
  for (const s of [-1, 1]) {
    const from = new THREE.Vector2(s * 0.8, hz - 0.3);
    const to = new THREE.Vector2(s * 0.35, T.z + 3.0);
    const mid = from.clone().add(to).multiplyScalar(0.5);
    bar(
      0.025,
      0.004,
      from.distanceTo(to),
      mid.x,
      0.006,
      mid.y,
      Math.atan2(to.x - from.x, to.y - from.y),
    );
  }

  /* ---------- Walls, ceiling, the dashboards' backdrop, atmosphere ---------- */
  // The hall's walls with the ward's arch open, the ward room's walls at its own height, and
  // above the arch a riser when the ward room's ceiling is higher than the hall's.
  const walls = buildWalls(time, WARM, [
    ...hallWallRuns().map((points) => ({ points })),
    { points: room.walls, top: room.height },
    ...(room.height > height + 0.01
      ? [
          {
            points: [
              { x: arch.x, z: arch.z0 },
              { x: arch.x, z: arch.z1 },
            ],
            bottom: height,
            top: room.height,
          },
        ]
      : []),
  ]);
  // Glossy Phong, not physically based metal with reflections: the rings fill much of the
  // upper view and full PBR shading cost a third of the room's frame (measured).
  const metal = new THREE.MeshPhongMaterial({
    color: C.champagne,
    specular: '#fff1d6',
    shininess: 60,
    // A warm glow standing in for light bounced up from the room.
    emissive: '#4a3a26',
  });
  const ceiling = buildCeiling(time, T, accent, metal, [
    { outline: hallOutline(), height },
    { outline: room.floor, height: room.height },
  ]);
  // The dashboards' wall: the left and right groups, one alcove per panel, and a taller
  // alcove of its own behind the centre screen.
  const wallH = ROOM.panelHeight + 0.52;
  const backdrop = buildBackdrop(
    time,
    A,
    ROOM.arcRadius + 0.22,
    [
      { from: plan.start, to: plan.angles[ARC.leftCount - 1]! + plan.pitch / 2 },
      { from: plan.angles[ARC.leftCount]! - plan.pitch / 2, to: plan.end },
    ],
    ROOM.panelCentreY - ROOM.panelHeight / 2 - 0.22,
    ROOM.panelCentreY + ROOM.panelHeight / 2 + 0.3,
    {
      pitch: plan.pitch,
      half: ROOM.panelWidth / 2 / ROOM.arcRadius / plan.pitch,
      v0: 0.22 / wallH,
      v1: (0.22 + ROOM.panelHeight) / wallH,
    },
  );
  // Behind the centre screen the wall is flat (a chord of its circle, 2026-10-07): the 9 m
  // screen sits flush in a tall alcove of its own there.
  const centreWall = { bottom: CENTRE.centreY - CENTRE.height / 2 - 0.3, top: 5.4 };
  const flat = centreWallPlan().half;
  const flatRadius = NORTH_WALL.radius - 0.02;
  const flatLength = 2 * flatRadius * Math.sin(flat);
  const centreBackdrop = buildBackdrop(
    time,
    A,
    flatRadius,
    [{ from: CENTRE.angle - flat, to: CENTRE.angle + flat, straight: true }],
    centreWall.bottom,
    centreWall.top,
    {
      pitch: flat * 2,
      half: CENTRE.width / 2 / flatLength,
      v0: 0.3 / (centreWall.top - centreWall.bottom),
      v1: (0.3 + CENTRE.height) / (centreWall.top - centreWall.bottom),
      metres: flatLength,
    },
  );
  centreBackdrop.name = 'chamber-centre-backdrop';
  // The data horizon above the arc: the zone modules frame the dashboard groups, words of
  // purpose sit between them and the centre screen, which rises into the band.
  const deg = (d: number) => (d * Math.PI) / 180;
  // On the curved wall itself (2026-10-07): the band glazes the wall above the backdrop.
  // The modules sit over each group's outer panels, the words over its inner ones; the band
  // stops at the flat wall behind the centre screen.
  const inner = { left: CENTRE.angle - plan.centreHalf, right: CENTRE.angle + plan.centreHalf };
  const horizon = buildHorizon(time, {
    centre: A,
    radius: ROOM.arcRadius + 0.1,
    left: { from: plan.start + deg(7), to: plan.start + deg(43), y0: 3.05, y1: 4.25 },
    right: { from: plan.end - deg(43), to: plan.end - deg(7), y0: 3.05, y1: 4.25 },
    words: [
      { from: inner.left - deg(24), to: inner.left - deg(4), y0: 3.1, y1: 4.2 },
      { from: inner.right + deg(4), to: inner.right + deg(24), y0: 3.1, y1: 4.2 },
    ],
    band: {
      from: plan.start - deg(2),
      to: plan.end + deg(2),
      y0: 2.94,
      y1: 4.78,
      gap: { from: CENTRE.angle - flat, to: CENTRE.angle + flat },
    },
  });
  // Ribs: curved champagne blades in the gaps between dashboard groups and at the arc's
  // ends, rising past the horizon and bending in toward the ceiling, each with a line of
  // light on its inner face.
  // The inner two stand where the flat wall behind the centre screen meets the curve.
  for (const a of [
    plan.start - deg(1.2),
    CENTRE.angle - flat,
    CENTRE.angle + flat,
    plan.end + deg(1.2),
  ]) {
    const at = (r: number, y: number) =>
      new THREE.Vector3(A.x + Math.cos(a) * r, y, A.z + Math.sin(a) * r);
    const curve = (inset: number) =>
      new THREE.CatmullRomCurve3([
        at(ROOM.arcRadius + 0.12 - inset, 0.0),
        at(ROOM.arcRadius + 0.12 - inset, 2.5),
        at(ROOM.arcRadius + 0.12 - inset, 4.6),
        at(ROOM.arcRadius - 0.05 - inset, 5.15),
        at(ROOM.arcRadius - 0.5 - inset, 5.45),
      ]);
    shell.place(new THREE.TubeGeometry(curve(0), 40, 0.045, 8), 0, 0, 0, {
      colour: C.champagne,
      cls: 'metal',
    });
    glow.push(new THREE.TubeGeometry(curve(0.05), 40, 0.01, 5));
  }
  const atmosphere = buildAtmosphere(time, T, accent);

  // Entrance: frosted crystal glass either side of a bronze door, a stone lintel above.
  shell.box(width - 2.4, 1.0, 0.3, 0, height - 0.5, hz + 0.15, { colour: C.graphite, noAo: true });
  for (const side of [-1, 1]) {
    shell.box(hx - 2.9, height - 1, 0.08, side * (hx / 2 + 0.25), (height - 1) / 2, hz + 0.04, {
      colour: C.glass,
      cls: 'glass',
    });
  }
  // Lines of light down the glass, except behind the presentation display.
  const display = {
    x0: PRESENTATION.x - PRESENTATION.width / 2,
    x1: PRESENTATION.x + PRESENTATION.width / 2,
  };
  for (let x = -hx + 1.2; x <= hx - 1.2 + 0.01; x += 2.4)
    if (Math.abs(x) > 1.9 && (x < display.x0 - 0.4 || x > display.x1 + 0.4))
      bar(0.02, height - 1.1, 0.02, x, (height - 1) / 2, hz - 0.02);
  shell.box(0.2, 3.4, 0.38, -1.75, 1.7, hz + 0.05, { colour: C.bronze, cls: 'metal' });
  shell.box(0.2, 3.4, 0.38, 1.75, 1.7, hz + 0.05, { colour: C.bronze, cls: 'metal' });
  shell.box(3.7, 0.2, 0.38, 0, 3.5, hz + 0.05, { colour: C.bronze, cls: 'metal' });
  // The doorway: a frosted crystal veil, lit softly from within (it leads to the lobby).
  shell.box(3.3, 3.4, 0.03, 0, 1.7, hz + 0.2, { colour: '#7c736a', cls: 'glass' });
  bar(3.1, 0.02, 0.02, 0, 3.37, hz - 0.16);

  /* ---------- The table: a sculpted disc of warm stone on a flared titanium stem ---------- */
  const lathe = (pts: [number, number][], segments = 72) =>
    new THREE.LatheGeometry(
      pts.map(([r, y]) => new THREE.Vector2(r, y)),
      segments,
    );
  shell.place(
    lathe([
      [0.0, 0.86],
      [1.32, 0.86],
      [1.46, 0.88],
      [1.52, 0.92],
      [1.5, 0.96],
      [1.4, 0.975],
      [0.0, 0.975],
    ]),
    T.x,
    0,
    T.z,
    { colour: C.tableTop, noAo: true },
  );
  shell.place(
    lathe([
      [0.0, 0.0],
      [0.72, 0.0],
      [0.7, 0.03],
      [0.3, 0.09],
      [0.16, 0.3],
      [0.14, 0.6],
      [0.3, 0.84],
      [0.0, 0.86],
    ]),
    T.x,
    0,
    T.z,
    { colour: C.titanium, cls: 'metal' },
  );
  cool.push(
    new THREE.TorusGeometry(1.515, 0.012, 6, 180).rotateX(Math.PI / 2).translate(T.x, 0.925, T.z),
  );
  coolRing(1.25, 0.012, T.x, 0.977, T.z);
  // The plinth for the table's miniature 3D chart: a slim titanium stem and a disc of frosted
  // glass edged with light, just beyond the room console (see TABLE_STAGE).
  {
    const pl = TABLE_STAGE.plinth;
    const top = TABLE_STAGE.y;
    shell.place(
      new THREE.CylinderGeometry(0.018, 0.03, top - 0.97, 12),
      pl.x,
      (top + 0.97) / 2,
      pl.z,
      {
        colour: C.titanium,
        cls: 'metal',
      },
    );
    shell.place(
      new THREE.CylinderGeometry(pl.radius, pl.radius, 0.012, 48),
      pl.x,
      top - 0.006,
      pl.z,
      {
        colour: C.glass,
        cls: 'glass',
      },
    );
    coolRing(pl.radius, 0.012, pl.x, top + 0.001, pl.z);
  }

  /* ---------- Glass helix columns either side of the entrance ---------- */
  const columnSpots = [
    { x: -2.35, z: hz - 0.7 },
    { x: 2.35, z: hz - 0.7 },
  ];
  for (const c of columnSpots) {
    shell.place(new THREE.CylinderGeometry(0.24, 0.24, 2.7, 40, 1, true), c.x, 1.5, c.z, {
      colour: C.glass,
      cls: 'glass',
    });
    shell.place(new THREE.CylinderGeometry(0.3, 0.32, 0.16, 40), c.x, 0.08, c.z, {
      colour: C.bronze,
      cls: 'metal',
    });
    shell.place(new THREE.CylinderGeometry(0.3, 0.3, 0.1, 40), c.x, 2.9, c.z, {
      colour: C.bronze,
      cls: 'metal',
    });
    coolRing(0.33, 0.015, c.x, 0.165, c.z);
  }
  const helices = buildHelixColumns(time, columnSpots, 2.4, 0.3);
  const plantSpots = [
    // The studio's corner by the opening, beside the model shelf.
    { x: -hx - 0.75, z: STUDIO.z0 + 0.65, scale: 1.1 },
    { x: -3.3, z: hz - 0.65 },
    { x: 3.3, z: hz - 0.65 },
  ];
  // Planters join the shell's metal; the leaves are one instanced mesh.
  for (const sp of plantSpots) {
    const k = sp.scale ?? 1;
    shell.place(new THREE.CylinderGeometry(0.26 * k, 0.22 * k, 0.5 * k, 32), sp.x, 0.25 * k, sp.z, {
      colour: C.titanium,
      cls: 'metal',
    });
  }
  const plants = buildPlants(plantSpots, false);

  /* ---------- The studio's whiteboard, the presentation display, the lectern ---------- */
  // The whiteboard on the studio's back wall: a pale backing, a champagne pen ledge, a line of
  // light above it.
  const sx0 = STUDIO.x0;
  const boardZ = STUDIO_FITTINGS.boardZ;
  shell.box(0.06, 2.0, 6, sx0 + 0.03, 1.6, boardZ, { colour: C.whiteboard, noAo: true });
  shell.box(0.08, 0.05, 6.1, sx0 + 0.05, 0.575, boardZ, { colour: C.champagne, cls: 'metal' });
  bar(0.02, 0.02, 6.1, sx0 + 0.08, 2.63, boardZ);
  // The opening into the studio: champagne jambs and a light along the floor's edge.
  for (const z of [STUDIO.z0, STUDIO.z1])
    shell.box(0.36, height, 0.3, -hx, height / 2, z, { colour: C.champagne, cls: 'metal' });
  bar(0.02, 0.004, STUDIO.z1 - STUDIO.z0 - 0.4, -hx, 0.006, (STUDIO.z0 + STUDIO.z1) / 2);
  // The presentation display on the entrance wall, on a dark stone panel against the glass.
  const P = PRESENTATION;
  shell.box(P.width + 0.6, P.height + 0.7, 0.08, P.x, P.y + 0.1, hz - 0.05, {
    colour: C.stoneDark,
    noAo: true,
  });
  shell.box(P.width, P.height, 0.06, P.x, P.y, hz - 0.1, { colour: C.screen, noAo: true });
  bar(P.width + 0.1, 0.02, 0.02, P.x, P.y + P.height / 2 + 0.03, hz - 0.13);
  bar(P.width + 0.1, 0.02, 0.02, P.x, P.y - P.height / 2 - 0.03, hz - 0.13);
  // The lectern: a stone column on a bronze foot with a champagne reading top.
  const lecternPosition = new THREE.Vector3(PRESENTATION.lectern.x, 0, PRESENTATION.lectern.z);
  shell.box(0.44, 1.12, 0.48, lecternPosition.x, 0.6, lecternPosition.z, {
    colour: C.stone,
    rotY: PRESENTATION.lectern.yaw,
    noAo: true,
  });
  shell.box(0.6, 0.05, 0.62, lecternPosition.x, 0.025, lecternPosition.z, {
    colour: C.bronze,
    cls: 'metal',
    rotY: PRESENTATION.lectern.yaw,
  });
  shell.box(0.55, 0.04, 0.6, lecternPosition.x, 1.17, lecternPosition.z, {
    colour: C.champagne,
    cls: 'metal',
    rotation: new THREE.Euler(0.25, PRESENTATION.lectern.yaw, 0, 'YXZ'),
  });
  {
    const back = new THREE.Vector3(0, 0, -0.245).applyAxisAngle(
      new THREE.Vector3(0, 1, 0),
      PRESENTATION.lectern.yaw,
    );
    bar(
      0.02,
      0.9,
      0.015,
      lecternPosition.x + back.x,
      0.62,
      lecternPosition.z + back.z,
      PRESENTATION.lectern.yaw,
    );
  }

  /* ---------- Reception: Luma, the host, just inside the entrance ---------- */
  // A stone counter turned toward the spawn point, with the host standing behind it.
  {
    const r = RECEPTION;
    const cw = r.counter.width;
    const at = (x: number, z: number) => receptionPoint(x, z);
    const c = at(0, 0);
    shell.box(
      cw,
      r.counter.height - 0.05,
      r.counter.depth,
      c.x,
      (r.counter.height - 0.05) / 2,
      c.z,
      {
        colour: C.stone,
        noAo: true,
        rotY: r.yaw,
      },
    );
    shell.box(cw + 0.1, 0.05, r.counter.depth + 0.1, c.x, r.counter.height - 0.025, c.z, {
      colour: C.champagne,
      cls: 'metal',
      rotY: r.yaw,
    });
    shell.box(cw + 0.1, 0.03, r.counter.depth + 0.1, c.x, 0.015, c.z, {
      colour: C.bronze,
      cls: 'metal',
      rotY: r.yaw,
    });
    const foot = at(0, r.counter.depth / 2 + 0.01);
    bar(cw - 0.12, 0.015, 0.015, foot.x, 0.08, foot.z, r.yaw);
    const lip = at(0, r.counter.depth / 2 + 0.051);
    bar(cw + 0.06, 0.012, 0.012, lip.x, r.counter.height - 0.03, lip.z, r.yaw);
    // The host's spot: a ring of light on the floor behind the counter.
    const h = at(r.host.x, r.host.z);
    coolRing(0.32, 0.014, h.x, 0.008, h.z);
  }

  /* ---------- Patient ward ---------- */
  // A theatre on the east side whose rows run on through the arch into the ward room. The
  // plinths, rings, mannequins and console are the Ward layer's; the shell has the darker stone
  // dais under the whole fan, the focal ring, a line of light along the dais's front edge, and
  // the arch: champagne jambs and a beam carrying the sign.
  {
    const dais = new THREE.ShapeGeometry(planShape(room.dais), 4).rotateX(-Math.PI / 2);
    shell.place(dais, 0, 0.012, 0, { colour: C.stoneDark, cls: 'floor' });
    const f = WARD_AREA.focal;
    const h = WARD_AREA.maxHalfAngle;
    ring(0.46, 0.04, f.x, 0.046, f.z);
    ring(1.9, 0.025, f.x, 0.02, f.z, -h, h);
    for (const z of [arch.z0, arch.z1])
      shell.box(0.4, room.height, 0.36, arch.x, room.height / 2, z, {
        colour: C.champagne,
        cls: 'metal',
      });
    shell.box(0.4, 0.22, arch.z1 - arch.z0, arch.x, height - 0.11, f.z, {
      colour: C.champagne,
      cls: 'metal',
    });
    bar(0.02, 0.02, arch.z1 - arch.z0 - 0.4, arch.x - 0.21, height - 0.24, f.z);
  }

  /* ---------- The centre screen ---------- */
  // A flat screen a little inside the arc so its edges stay in front of the backdrop. Light outlines it like the alcoves; a
  // champagne sill runs under it.
  const centrePoint = new THREE.Vector3(
    A.x + Math.cos(CENTRE.angle) * (ROOM.arcRadius - CENTRE.inset),
    CENTRE.centreY,
    A.z + Math.sin(CENTRE.angle) * (ROOM.arcRadius - CENTRE.inset),
  );
  const centreYaw = Math.atan2(A.x - centrePoint.x, A.z - centrePoint.z);
  const centrePosition = centrePoint.clone();
  {
    const along = new THREE.Vector3(Math.cos(centreYaw), 0, -Math.sin(centreYaw));
    const out = new THREE.Vector3(Math.sin(centreYaw), 0, Math.cos(centreYaw));
    const at = (u: number, y: number, depth: number) =>
      centrePoint.clone().addScaledVector(along, u).addScaledVector(out, depth).setY(y);
    const w = CENTRE.width;
    const h = CENTRE.height;
    const y0 = CENTRE.centreY - h / 2;
    const y1 = CENTRE.centreY + h / 2;
    // Lines of light just outside the screen's edges, set back so the glass covers them.
    for (const [u, y, lw, lh] of [
      [0, y1 + 0.04, w + 0.12, 0.012],
      [0, y0 - 0.04, w + 0.12, 0.012],
      [-w / 2 - 0.05, CENTRE.centreY, 0.012, h + 0.1],
      [w / 2 + 0.05, CENTRE.centreY, 0.012, h + 0.1],
    ] as const) {
      const c = at(u, y, -0.04);
      bar(lw, lh, 0.012, c.x, c.y, c.z, centreYaw);
    }
    const sill = at(0, y0 - 0.12, 0.02);
    shell.box(w + 0.3, 0.05, 0.16, sill.x, sill.y, sill.z, {
      colour: C.champagne,
      cls: 'metal',
      rotY: centreYaw,
    });
  }

  /* ---------- Dashboard positions on the arc ---------- */
  // The panels are set into the backdrop: no stands. Each keeps its placeholder face, title,
  // status light and grab handle, and a line of light beneath it.
  const panels: ArcPanel[] = [];
  const panelGroup = new THREE.Group();
  const faceMaterial = new THREE.MeshStandardMaterial({
    color: '#3a3531',
    roughness: 0.4,
    metalness: 0.2,
    transparent: true,
    opacity: 0.85,
  });
  const lightMaterial = new THREE.MeshStandardMaterial({
    color: accent,
    emissive: accent,
    emissiveIntensity: 1.2,
  });
  const handleMaterial = new THREE.MeshPhongMaterial({
    color: C.champagne,
    specular: '#fff1d6',
    shininess: 60,
  });
  DASHBOARD_ARC_ORDER.forEach((id, i) => {
    const angle = plan.angles[i]!;
    const x = A.x + Math.cos(angle) * ROOM.arcRadius;
    const z = A.z + Math.sin(angle) * ROOM.arcRadius;
    const yaw = Math.atan2(A.x - x, A.z - z);
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    g.rotation.y = yaw;
    const local = (lx: number, ly: number, lz: number) =>
      new THREE.Vector3(lx, ly, lz)
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw)
        .add(new THREE.Vector3(x, 0, z));
    const strip = local(0, ROOM.panelCentreY - ROOM.panelHeight / 2 - 0.07, 0.01);
    bar(ROOM.panelWidth - 0.2, 0.012, 0.012, strip.x, strip.y, strip.z, yaw);
    const face = new THREE.Mesh(
      new THREE.PlaneGeometry(ROOM.panelWidth, ROOM.panelHeight),
      faceMaterial,
    );
    face.position.set(0, ROOM.panelCentreY, 0);
    face.name = `panel-${id}`;
    const title = makeSign({
      lines: [{ text: DASHBOARD_TITLES[id], size: 44, weight: 600, colour: '#f3ede4' }],
      width: ROOM.panelWidth,
      height: 0.18,
      background: '#3a3531',
      backgroundOpacity: 0.92,
      accent: theme.accent,
      radius: 10,
    });
    title.position.set(0, ROOM.panelCentreY + ROOM.panelHeight / 2 - 0.1, 0.012);
    const statusLight = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 8), lightMaterial);
    statusLight.position.set(
      ROOM.panelWidth / 2 - 0.08,
      ROOM.panelCentreY + ROOM.panelHeight / 2 - 0.1,
      0.03,
    );
    const handle = new THREE.Mesh(new THREE.CapsuleGeometry(0.02, 0.2, 4, 10), handleMaterial);
    handle.rotation.z = Math.PI / 2;
    handle.position.set(0, ROOM.panelCentreY - ROOM.panelHeight / 2 - 0.16, 0.03);
    g.add(face, title, statusLight, handle);
    panelGroup.add(g);
    panels.push({
      id,
      title: DASHBOARD_TITLES[id],
      group: g,
      face,
      statusLight,
      handle,
      sign: title,
      position: new THREE.Vector3(x, ROOM.panelCentreY, z),
      yaw,
    });
  });

  const group = shell.build();
  group.name = `room-${theme.code}`;
  // The shell's metal is glossy Phong (cheap); glass is frosted.
  for (const c of group.children) {
    const m = c as THREE.Mesh;
    if (m.name === 'shell-metal') {
      (m.material as THREE.Material).dispose();
      m.material = new THREE.MeshPhongMaterial({
        vertexColors: true,
        specular: '#fff1d6',
        shininess: 55,
        emissive: '#2e2418',
      });
    }
    if (m.name === 'shell-glass') {
      const g = m.material as THREE.MeshStandardMaterial;
      g.roughness = 0.55;
      g.opacity = 0.5;
    }
  }

  group.add(
    floor,
    walls,
    ceiling.group,
    backdrop,
    centreBackdrop,
    atmosphere,
    panelGroup,
    horizon.group,
    helices,
    plants,
  );

  const glowMaterial = new THREE.MeshBasicMaterial({ color: WARM, toneMapped: false });
  const coolMaterial = new THREE.MeshBasicMaterial({ color: accent, toneMapped: false });
  const glowMesh = new THREE.Mesh(
    mergeGeometries(glow.map((g) => g.toNonIndexed()))!,
    glowMaterial,
  );
  glowMesh.name = 'chamber-light-lines';
  group.add(glowMesh);
  for (const g of glow) g.dispose();

  // Chairs (2026-10-07, the user's reference render): barrel lounge chairs in taupe fabric with
  // cream piping and a leather band, on bronze cones with a ring of warm light at the foot
  // (world/chair.ts), open toward the table. Three draw calls for all eight.
  const seatPositions = Array.from({ length: 8 }, (_, i) => {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const x = T.x + Math.cos(a) * 2.45;
    const z = T.z + Math.sin(a) * 2.45;
    return { position: { x, y: 0, z }, rotY: Math.atan2(T.x - x, T.z - z) };
  });
  const chair = buildChairGeometry();
  const seats = instanced(
    chair.body,
    new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide }),
    seatPositions,
  );
  seats.name = 'chamber-chairs';
  const feet = instanced(
    chair.base,
    // Satin bronze, as in the reference: a soft sheen, no hot spot.
    new THREE.MeshPhongMaterial({ vertexColors: true, specular: '#6e5a46', shininess: 28 }),
    seatPositions,
  );
  feet.name = 'chamber-chair-bases';
  const chairLights = instanced(chair.light, glowMaterial, seatPositions);
  chairLights.name = 'chamber-chair-lights';
  const coolMesh = new THREE.Mesh(
    mergeGeometries(cool.map((g) => g.toNonIndexed()))!,
    coolMaterial,
  );
  coolMesh.name = 'chamber-study-lines';
  group.add(coolMesh);
  for (const g of cool) g.dispose();
  group.add(seats, feet, chairLights);

  // Zone labels. (The study briefing by the entrance was retired on 2026-10-07: what it showed
  // is on the centre screen's summary.)
  const zoneSign = (text: string, x: number, y: number, z: number, rotY: number) => {
    const s = makeSign({
      lines: [{ text, size: 46, weight: 600, colour: C.sign, letterSpacing: 6 }],
      width: 2.4,
      height: 0.3,
      backgroundOpacity: 0,
      align: 'center',
    });
    s.position.set(x, y, z);
    s.rotation.y = rotY;
    group.add(s);
  };
  zoneSign('WHITEBOARD', STUDIO.x0 + 0.08, 2.85, STUDIO_FITTINGS.boardZ, Math.PI / 2);
  zoneSign('COLLABORATION STUDIO', STUDIO.x0 + 0.08, 4.3, (STUDIO.z0 + STUDIO.z1) / 2, Math.PI / 2);
  zoneSign(
    'PRESENTATION',
    PRESENTATION.x,
    PRESENTATION.y + PRESENTATION.height / 2 + 0.32,
    hz - 0.12,
    Math.PI,
  );
  // On the arch's beam, facing the hall.
  zoneSign('PATIENT WARD', arch.x - 0.21, height - 0.5, WARD_AREA.focal.z, -Math.PI / 2);
  {
    const sign = receptionPoint(0, -1.05);
    zoneSign('AI ASSISTANT', sign.x, 2.55, sign.z, RECEPTION.yaw);
  }
  const exitSign = makeSign({
    lines: [{ text: 'EXIT TO LOBBY', size: 44, weight: 600, colour: C.sign, letterSpacing: 6 }],
    width: 2.4,
    height: 0.3,
    backgroundOpacity: 0,
    align: 'center',
  });
  exitSign.position.set(0, 3.8, hz - 0.3);
  exitSign.rotation.y = Math.PI;
  group.add(exitSign);
  const logo = makeSign({
    lines: [
      { text: theme.code, size: 110, weight: 700, colour: C.sign },
      { text: 'Clinical Intelligence Chamber', size: 36, colour: '#b8ad9f' },
    ],
    width: 3.6,
    height: 1.0,
    backgroundOpacity: 0,
    align: 'center',
  });
  logo.position.set(7, 3.6, hz - 0.1);
  logo.rotation.y = Math.PI;
  group.add(logo);

  // Light: a warm key from above and a warm fill; the rest comes from the architecture.
  const sun = new THREE.DirectionalLight(LIGHT.sun, LIGHT.intensity);
  sun.position.set(...LIGHT.position);
  group.add(sun);
  group.add(new THREE.HemisphereLight('#fff0dc', '#4e443c', LIGHT.fill));

  // Where the centre screen will stand: the study and a loading or error line until the
  // dashboards arrive (it was the briefing board's footer).
  const status = buildStatusBoard(theme, centrePosition, centreYaw);
  group.add(status.mesh);

  // Feet stay inside the hall, the studio and the ward room (the outline with a margin), off
  // the furniture.
  const outline = walkableOutline(room);
  const shelf = STUDIO_FITTINGS.shelf;
  const walkable: WalkableBounds = {
    minX: Math.min(...outline.map((p) => p.x)),
    maxX: Math.max(...outline.map((p) => p.x)),
    minZ: Math.min(...outline.map((p) => p.z)),
    maxZ: Math.max(...outline.map((p) => p.z)),
    polygon: { points: outline, margin: 0.5 },
    blocked: [
      {
        minX: T.x - 1.6,
        maxX: T.x + 1.6,
        minZ: T.z - 1.6,
        maxZ: T.z + 1.6,
      },
      // The model shelf against the studio's north wall, and the whiteboard's tray.
      { minX: shelf.x - 0.9, maxX: shelf.x + 0.9, minZ: STUDIO.z0, maxZ: shelf.z + 0.4 },
      {
        minX: STUDIO.x0 + 0.35,
        maxX: STUDIO.x0 + 0.95,
        minZ: STUDIO_FITTINGS.trayZ - 0.3,
        maxZ: STUDIO_FITTINGS.trayZ + 0.3,
      },
      {
        minX: PRESENTATION.lectern.x - 0.4,
        maxX: PRESENTATION.lectern.x + 0.4,
        minZ: PRESENTATION.lectern.z - 0.4,
        maxZ: PRESENTATION.lectern.z + 0.4,
      },
      // The reception counter and the host behind it.
      (() => {
        const pts = [
          [-1.0, -0.95],
          [1.0, -0.95],
          [-1.0, 0.33],
          [1.0, 0.33],
        ].map(([x, z]) => receptionPoint(x!, z!));
        return {
          minX: Math.min(...pts.map((q) => q.x)),
          maxX: Math.max(...pts.map((q) => q.x)),
          minZ: Math.min(...pts.map((q) => q.z)),
          maxZ: Math.max(...pts.map((q) => q.z)),
        };
      })(),
      ...[...columnSpots, ...plantSpots].map((c) => ({
        minX: c.x - 0.4,
        maxX: c.x + 0.4,
        minZ: c.z - 0.4,
        maxZ: c.z + 0.4,
      })),
    ],
  };

  return {
    group,
    bounds: walkable,
    floors: [floor, ...group.children.filter((c) => c.name === 'shell-floor')],
    panels,
    exitZone: { minX: -1.3, maxX: 1.3, minZ: hz - 0.6, maxZ: hz + 1 },
    lecternPosition,
    wardRoom: room,
    centreScreen: { position: centrePosition, yaw: centreYaw, scale: CENTRE.scale },
    setHeader: (data) => horizon.set(data),
    setStatus: (text, isError) => status.set(text, isError),
    update: (dt) => {
      time.value += dt;
      ceiling.update(dt);
    },
    dispose: () => {
      horizon.dispose();
      status.dispose();
    },
  };
}

/**
 * A plain canvas the size of the centre screen, just in front of its place: the study's code
 * and title over a loading or error line. Redrawn only when the line changes.
 */
function buildStatusBoard(
  theme: StudyTheme,
  at: THREE.Vector3,
  yaw: number,
): { mesh: THREE.Mesh; set(text: string | undefined, isError?: boolean): void; dispose(): void } {
  const W = 1200;
  const H = Math.round((W * CENTRE.height) / CENTRE.width);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(CENTRE.width, CENTRE.height),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true, toneMapped: false }),
  );
  mesh.name = 'centre-status';
  mesh.position
    .copy(at)
    .add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(0.03));
  mesh.rotation.y = yaw;
  mesh.visible = false;
  let shown = '';
  return {
    mesh,
    set: (line, isError) => {
      mesh.visible = !!line;
      const key = `${line}|${isError}`;
      if (!line || key === shown) return;
      shown = key;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(30, 27, 24, 0.82)';
      ctx.fillRect(0, 0, W, H);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#f3ede4';
      ctx.font = `700 120px ${DISPLAY_FONT}`;
      ctx.fillText(theme.code, W / 2, H * 0.36);
      ctx.fillStyle = '#cdb58f';
      ctx.font = `500 40px ${BODY_FONT}`;
      ctx.fillText(theme.shortTitle, W / 2, H * 0.5, W - 120);
      ctx.fillStyle = isError ? '#ff7a6b' : '#7fdcff';
      ctx.font = `600 44px ${BODY_FONT}`;
      ctx.fillText(line, W / 2, H * 0.7, W - 120);
      texture.needsUpdate = true;
    },
    dispose: () => {
      texture.dispose();
      (mesh.material as THREE.Material).dispose();
      mesh.geometry.dispose();
    },
  };
}

/**
 * A study colour as soft architectural light: the same hue, gentle saturation and a calm
 * brightness, so seams glow without turning electric. Study colours chosen for light rooms
 * (CARD-317's navy) would otherwise vanish.
 */
function luminousSoft(hex: string): string {
  const c = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(hsl.h, Math.min(0.62, Math.max(0.45, hsl.s)), Math.max(0.58, Math.min(0.68, hsl.l)));
  return `#${c.getHexString()}`;
}
